import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import dayjs from "dayjs";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { getPublicConfig } from "../../../api/config/getPublicConfig";
import { getDossier } from "../../../api/dossier/getDossier";
import { getDossierTransactionComparison } from "../../../api/dossier/getDossierTransactionComparison";
import APSelectDropDown, {
  SelectDropdownOption,
} from "../../../components/APSelectDropDown";
import { APButtonLink } from "../../../components/buttons/APButtonLink";
import CatchEButton from "../../../components/buttons/CatchEButton";
import CRMButton from "../../../components/buttons/CRMButton";
import RepairDossierTransactionsButton from "../../../components/buttons/RepairDossierTransactionsButton";
import SyncDossierTransactionsButton from "../../../components/buttons/SyncDossierTransactionsButton";
import APDateField from "../../../components/form/APDateField";
import PageContainer from "../../../components/PageContainer";
import { getCRMClientURL } from "../../../helpers/configuration";
import { toDollars } from "../../../helpers/currencies";
import { DateTimeFormat, formatDate } from "../../../helpers/dates";
import { getDossierCRMUrl } from "../../../helpers/dossiers";
import { useAuth } from "../../../providers/AuthProvider";
import {
  DossierTransactionStatus,
  DossierTransactionType,
  filterTransactionComparisons,
  sortTransactionComparisons,
  TransactionComparisonSortOption,
} from "../../../types/DossierTransaction";
import APTransactionComparisonTable from "./components/APTransactionComparisonTable";

type transactionComparisonFilters = {
  transactionStatus: DossierTransactionStatus | null;
  comparisonStatus: "matching" | "mismatching" | "missing" | null;
  transactionType: DossierTransactionType | null;
};

const transactionComparisonSortOptions: SelectDropdownOption<TransactionComparisonSortOption>[] =
  [
    { id: "date", label: "Date" },
    { id: "status", label: "Status" },
  ];

const transactionTypeOptions: SelectDropdownOption<
  DossierTransactionType | "all"
>[] = [
  { id: "fuel", label: "Fuel" },
  { id: "servicing", label: "Servicing" },
  { id: "tyres", label: "Tyres" },
  { id: "rego", label: "Rego" },
  { id: "insurance", label: "Insurance" },
  { id: "finance", label: "Finance" },
  { id: "fees", label: "Fees" },
  { id: "pre-tax", label: "Pre Tax" },
  { id: "post-tax", label: "Post Tax" },
  { id: "all", label: "All" },
];

const transactionComparisonStatusOptions: SelectDropdownOption<
  DossierTransactionStatus | "all"
>[] = [
  { id: "unpaid", label: "Unpaid" },
  { id: "paid", label: "Paid" },
  { id: "payment-in-progress", label: "Payment in Progress" },
  { id: "all", label: "All" },
];

const comparisonStatusOptions: SelectDropdownOption<
  "matching" | "mismatching" | "missing" | "all"
>[] = [
  { id: "matching", label: "Matching" },
  { id: "mismatching", label: "Mismatching" },
  { id: "missing", label: "Missing" },
  { id: "all", label: "All" },
];

type TransactionSyncInputs = {
  dateFrom: string;
  dateTo: string;
};

const DossierTransactionComparison = () => {
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const { dossierId } = useParams<{
    dossierId: string;
  }>();
  const [
    transactionComparisonSortOptionId,
    setTransactionComparisonSortOptionId,
  ] = useState<TransactionComparisonSortOption>("status");
  const [transactionComparisonFilters, setTransactionComparisonFilters] =
    useState<transactionComparisonFilters>({
      transactionType: null,
      transactionStatus: null,
      comparisonStatus: null,
    });
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
    watch,
  } = useForm<TransactionSyncInputs>({
    mode: "onTouched",
  });
  const dateFrom = watch("dateFrom");
  const dateTo = watch("dateTo");

  const dossier = getDossier(dossierId ?? "");
  const config = getPublicConfig();

  const crmClientURL = config.isSuccess ? getCRMClientURL(config.data) : null;

  const {
    data: transactionComparison,
    isLoading: isLoadingTransactionComparison,
    isSuccess: isSuccessTransactionComparison,
  } = getDossierTransactionComparison(dossierId ?? "");

  const onTransactionComparisonSortChange = (
    optionId: TransactionComparisonSortOption
  ) => {
    setTransactionComparisonSortOptionId(optionId);
  };

  const onTransactionTypeChange = (
    optionId: DossierTransactionType | "all"
  ) => {
    setTransactionComparisonFilters({
      ...transactionComparisonFilters,
      transactionType:
        optionId === "all" ? null : (optionId as DossierTransactionType),
    });
  };

  const onTransactionComparisonStatusChange = (
    optionId: DossierTransactionStatus | "all"
  ) => {
    setTransactionComparisonFilters({
      ...transactionComparisonFilters,
      transactionStatus:
        optionId === "all" ? null : (optionId as DossierTransactionStatus),
    });
  };

  const onComparisonStatusChange = (
    optionId: "matching" | "mismatching" | "missing" | "all"
  ) => {
    setTransactionComparisonFilters({
      ...transactionComparisonFilters,
      comparisonStatus:
        optionId === "all"
          ? null
          : (optionId as "matching" | "mismatching" | "missing"),
    });
  };

  const dropdowns = (
    <Box sx={{ my: 1, display: "flex", justifyContent: "start", columnGap: 1 }}>
      <Box sx={{ width: "168px", display: "flex" }}>
        <APSelectDropDown
          label="Sort By"
          selectedOptionId={transactionComparisonSortOptionId}
          options={transactionComparisonSortOptions}
          onOptionChange={onTransactionComparisonSortChange}
        />
      </Box>

      <Box sx={{ width: "168px", display: "flex" }}>
        <APSelectDropDown
          label="Transaction Type"
          selectedOptionId={
            transactionComparisonFilters.transactionType ?? "all"
          }
          options={transactionTypeOptions}
          onOptionChange={onTransactionTypeChange}
        />
      </Box>

      <Box sx={{ width: "168px", display: "flex" }}>
        <APSelectDropDown
          label="Transaction Status"
          selectedOptionId={
            transactionComparisonFilters.transactionStatus ?? "all"
          }
          options={transactionComparisonStatusOptions}
          onOptionChange={onTransactionComparisonStatusChange}
        />
      </Box>

      <Box sx={{ width: "168px", display: "flex" }}>
        <APSelectDropDown
          label="Comparison Status"
          selectedOptionId={
            transactionComparisonFilters.comparisonStatus ?? "all"
          }
          options={comparisonStatusOptions}
          onOptionChange={onComparisonStatusChange}
        />
      </Box>
    </Box>
  );

  const isLoading =
    isLoadingTransactionComparison || dossier.isLoading || config.isLoading;
  const isSuccess =
    isSuccessTransactionComparison && dossier.isSuccess && config.isSuccess;

  return (
    <PageContainer
      title="Transaction Comparison"
      loading={isLoading}
      size="full"
      titleButton={
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: 2 }}>
          <Box
            sx={{
              display: "flex",
              columnGap: 2,
              justifyContent: "end",
            }}
          >
            {isSuccess && dossier.data?.opportunity?.catchEContactUrl && (
              <>
                <Box
                  sx={{ display: "flex", alignItems: "center", columnGap: 1 }}
                >
                  <Box sx={{ display: "flex", columnGap: 1 }}>
                    <APDateField
                      name="dateFrom"
                      label={"From"}
                      control={control}
                      defaultValue={"2023-10-01"}
                      errors={errors}
                      setError={setError}
                      clearErrors={clearErrors}
                      maxDate={dateTo ? dayjs(dateTo) : undefined}
                      clearable
                    />
                    <APDateField
                      name="dateTo"
                      label={"To"}
                      control={control}
                      defaultValue={dayjs().add(1, "day").format("YYYY-MM-DD")}
                      errors={errors}
                      setError={setError}
                      clearErrors={clearErrors}
                      minDate={dateFrom ? dayjs(dateFrom) : undefined}
                      clearable
                    />
                  </Box>
                  <SyncDossierTransactionsButton
                    dossierId={dossierId ?? ""}
                    dateFrom={dateFrom ?? ""}
                    dateTo={dateTo ?? ""}
                  />
                </Box>
                <Box>
                  <CatchEButton
                    url={dossier.data.opportunity.catchEContactUrl}
                    sx={{ p: { sm: 1.5, xs: 1 } }}
                  />
                </Box>
                {crmClientURL && (
                  <Box>
                    <CRMButton
                      url={getDossierCRMUrl(crmClientURL, dossier.data.id)}
                      sx={{ p: { sm: 1.5, xs: 1 } }}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              columnGap: 2,
              justifyContent: "end",
              alignItems: "center",
            }}
          >
            <Box>
              <APButtonLink
                text="Invoice Comparison"
                onClick={() =>
                  navigate(
                    getScopedPageUrl(
                      "dossierInvoiceComparison",
                      dossierId ?? ""
                    )
                  )
                }
              />
            </Box>
            <RepairDossierTransactionsButton dossierId={dossierId ?? ""} />
          </Box>
        </Box>
      }
    >
      {isSuccess && (
        <Box mt={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box>{dropdowns}</Box>
            <Box sx={{ display: "flex", columnGap: 2 }} mt={1}>
              <Box>
                <Typography variant="bodyMedium" fontWeight={600}>
                  Current Balance
                </Typography>
                <Typography variant="bodyMedium">
                  {toDollars(dossier.data?.currentBalance ?? 0, 2)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="bodyMedium" fontWeight={600}>
                  Dossier Last Transaction Sync
                </Typography>
                <Typography variant="bodyMedium">
                  {dossier.data?.lastTransactionSyncDate
                    ? formatDate(
                        dossier.data.lastTransactionSyncDate,
                        DateTimeFormat
                      )
                    : ""}
                </Typography>
              </Box>
            </Box>
          </Box>
          <APTransactionComparisonTable
            transactionComparison={
              transactionComparison
                ? sortTransactionComparisons(
                    filterTransactionComparisons(
                      transactionComparison,
                      transactionComparisonFilters.transactionType,
                      transactionComparisonFilters.transactionStatus,
                      transactionComparisonFilters.comparisonStatus
                    ),
                    transactionComparisonSortOptionId
                  )
                : []
            }
            crmClientURL={crmClientURL}
          />
        </Box>
      )}
    </PageContainer>
  );
};

export default DossierTransactionComparison;
